import { BodyRegistry } from "../bodies/body_registry";
import { BodyType } from "../bodies/celestial_body_data";
import type { StateVector } from "../math/state_vector";
import { Vector3d } from "../math/vector3d";

/**
 * Result of an SOI transition check.
 */
export interface SOITransitionResult {
  transitioned: boolean;
  newSOIBody: string;
}

/**
 * Determines and handles sphere of influence transitions.
 */
export class SOIResolver {
  private readonly registry: BodyRegistry;

  constructor(registry: BodyRegistry) {
    this.registry = registry;
  }

  /**
   * Check if the spacecraft has transitioned to a different SOI.
   *
   * @param spacecraftHelioPos Spacecraft heliocentric position (km)
   * @param currentSOIBody Current SOI body name
   * @param jd Current Julian Date
   * @returns Transition result with new SOI body if transition occurred
   */
  checkTransition(
    spacecraftHelioPos: Vector3d,
    currentSOIBody: string,
    jd: number,
  ): SOITransitionResult {
    const current = this.registry.getByName(currentSOIBody);
    if (!current) return { transitioned: false, newSOIBody: currentSOIBody };

    // Check if we've left the current SOI
    if (current.bodyType !== BodyType.Star) {
      const positions = this.registry.computeAllPositions(jd);
      const currentPos = positions.get(currentSOIBody);
      if (currentPos) {
        const distFromCurrent = spacecraftHelioPos.subtract(currentPos).magnitude;
        if (distFromCurrent > current.soiRadius) {
          // Left current SOI - go to parent
          const parent = this.registry.getParent(current);
          return {
            transitioned: true,
            newSOIBody: parent?.name ?? "Sun",
          };
        }
      }
    }

    // Check if we've entered a child body's SOI
    const dominant = this.registry.findDominantBody(spacecraftHelioPos, jd);
    if (dominant.name !== currentSOIBody) {
      return {
        transitioned: true,
        newSOIBody: dominant.name,
      };
    }

    return { transitioned: false, newSOIBody: currentSOIBody };
  }

  /**
   * Convert spacecraft state from one reference frame to another during SOI transition.
   */
  transformToNewSOI(
    currentState: StateVector,
    oldBodyName: string,
    newBodyName: string,
    jd: number,
  ): StateVector {
    // Get heliocentric state of spacecraft
    let helioPos: Vector3d;
    let helioVel: Vector3d;

    if (oldBodyName === "Sun") {
      helioPos = currentState.position;
      helioVel = currentState.velocity;
    } else {
      const oldBodyState = this.registry.computeBodyState(oldBodyName, jd);
      helioPos = currentState.position.add(oldBodyState.position);
      helioVel = currentState.velocity.add(oldBodyState.velocity);
    }

    // Convert to new body's frame
    if (newBodyName === "Sun") {
      return {
        position: helioPos,
        velocity: helioVel,
        time: jd,
        centralBody: "Sun",
      };
    }

    const newBodyState = this.registry.computeBodyState(newBodyName, jd);
    const newBody = this.registry.getByName(newBodyName);

    let newBodyHelioPos: Vector3d;
    let newBodyHelioVel: Vector3d;

    if (newBody && newBody.bodyType === BodyType.Moon) {
      const parentState = this.registry.computeBodyState(newBody.parentBodyName!, jd);
      newBodyHelioPos = parentState.position.add(newBodyState.position);
      newBodyHelioVel = parentState.velocity.add(newBodyState.velocity);
    } else {
      newBodyHelioPos = newBodyState.position;
      newBodyHelioVel = newBodyState.velocity;
    }

    return {
      position: helioPos.subtract(newBodyHelioPos),
      velocity: helioVel.subtract(newBodyHelioVel),
      time: jd,
      centralBody: newBodyName,
    };
  }
}
